// apps/provider/src/components/calendar/calendarUtils.js
// Shared constants and utility functions for the custom time grid

import { normalizeWaitlistProviderIds, sortWaitlistEntries } from '../../utils/waitlistHelpers';
import { resolveScheduleTemplate, isDateWithinTemplateBounds } from '@prism-ehr/shared/utils/scheduleTemplateResolver';
import {
  buildAvailabilityMap,
  subtractAppointments,
  findCandidateStarts,
  isSlotAllowedByConstraints,
  normalizeTimePreferences,
  addDays as engineAddDays,
  localDateString,
} from '@prism-ehr/shared/utils/availabilityEngine';

// ============ Status Colors ============

export const STATUS_COLORS = {
  scheduled: '#0facd7',
  confirmed: '#3B82F6',
  checked_in: '#10B981',
  in_progress: '#8B5CF6',
  completed: '#6B7280',
  cancelled: '#EF4444',
  no_show: '#F59E0B',
  pending_approval: '#F97316',
  declined: '#DC2626',
};

export const STATUS_LABELS = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  checked_in: 'Checked In',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No Show',
  pending_approval: 'Pending',
  declined: 'Declined',
};

// ============ Texture Patterns (CSS background-image) ============

export const TEXTURE_PATTERNS = {
  dots: {
    backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.25) 1px, transparent 1px)',
    backgroundSize: '8px 8px',
  },
  diagonal_lines: {
    backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.2) 4px, rgba(255,255,255,0.2) 6px)',
    backgroundSize: 'auto',
  },
  crosshatch: {
    backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.15) 4px, rgba(255,255,255,0.15) 6px),
      repeating-linear-gradient(-45deg, transparent, transparent 4px, rgba(255,255,255,0.15) 4px, rgba(255,255,255,0.15) 6px)`,
    backgroundSize: 'auto',
  },
  horizontal_lines: {
    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 4px, rgba(255,255,255,0.2) 4px, rgba(255,255,255,0.2) 6px)',
    backgroundSize: 'auto',
  },
  none: {},
};

// ============ Unavailability Patterns ============

export const UNAVAILABILITY_PATTERNS = {
  outside_hours: {
    backgroundColor: '#D1D5DB',
    backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 6px, rgba(0,0,0,0.08) 6px, rgba(0,0,0,0.08) 8px)',
  },
  override_blocked: {
    backgroundColor: 'rgba(75, 85, 99, 0.6)',
    backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(0,0,0,0.1) 5px, rgba(0,0,0,0.1) 7px),
      repeating-linear-gradient(-45deg, transparent, transparent 5px, rgba(0,0,0,0.1) 5px, rgba(0,0,0,0.1) 7px)`,
  },
  note: {
    backgroundColor: '#FEF9C3',
    borderLeft: '3px solid #CA8A04',
  },
};

// ============ Time Utilities ============

/**
 * Convert "HH:mm" string to total minutes from midnight
 */
export function timeToMinutes(str) {
  if (!str) return 0;
  const [h, m] = str.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Convert total minutes from midnight to "HH:mm" string
 */
export function minutesToTime(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Union-merge two arrays of {start_time, end_time} blocks, collapsing overlaps.
 */
export function mergeDayBlocks(a = [], b = []) {
  const all = [...a, ...b]
    .filter(blk => blk && blk.start_time && blk.end_time)
    .map(blk => ({ start: timeToMinutes(blk.start_time), end: timeToMinutes(blk.end_time) }))
    .sort((x, y) => x.start - y.start);
  const out = [];
  for (const blk of all) {
    const last = out[out.length - 1];
    if (last && blk.start < last.end) {
      last.end = Math.max(last.end, blk.end);
    } else {
      out.push({ start: blk.start, end: blk.end });
    }
  }
  return out.map(o => ({ start_time: minutesToTime(o.start), end_time: minutesToTime(o.end) }));
}

/**
 * Merge multiple schedule_template objects into one by unioning blocks per day.
 * Used when a provider has availability rows at multiple locations and the
 * calendar has no location filter — shows when the person is working anywhere.
 */
export function mergeScheduleTemplates(templates = []) {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const result = {};
  for (const day of days) {
    let blocks = [];
    let is_available = false;
    for (const t of templates) {
      const d = t?.[day];
      if (!d) continue;
      if (d.is_available) is_available = true;
      if (Array.isArray(d.blocks) && d.blocks.length > 0) {
        blocks = mergeDayBlocks(blocks, d.blocks);
      }
    }
    result[day] = { is_available, blocks };
  }
  return result;
}

/**
 * The weekly template to paint for one provider on one date, across the
 * availability rows the calendar holds for them (one per location, or one
 * merged entry). Each row resolves past/current/pending for the date and is
 * dropped when the date falls outside that template's own bounds. One
 * surviving row returns its template as-is (bounds intact, consistent with the
 * date by construction); several are unioned exactly as before.
 * @param {Array} rows - provider_availability rows
 * @param {string} dateStr - YYYY-MM-DD
 * @returns {Object|null} template, or null when nothing applies (paints as unavailable)
 */
export function resolveTemplateForRows(rows, dateStr) {
  const resolved = [];
  for (const row of (rows || [])) {
    const template = resolveScheduleTemplate(row, dateStr);
    if (template && isDateWithinTemplateBounds(template, dateStr)) resolved.push(template);
  }
  if (resolved.length === 0) return null;
  if (resolved.length === 1) return resolved[0];
  return mergeScheduleTemplates(resolved);
}

/**
 * Format time for display (e.g. "9:00 AM")
 */
export function formatTimeDisplay(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${String(m).padStart(2, '0')} ${period}`;
}

// ============ Pixel-to-Time Conversion ============

/**
 * Convert a pixel Y offset within a time grid column to a snapped time slot
 * @param {number} y - Pixel offset from top of column
 * @param {string} slotMinTime - Grid start time "HH:mm"
 * @param {number} slotHeight - Height of one slot in px
 * @param {number} slotDuration - Duration of one slot in minutes
 * @param {number} [eventDuration] - Duration of the event in minutes (defaults to slotDuration)
 * @param {number} [snapInterval=5] - Snap granularity in minutes (org/location `click_snap_minutes`); passed by both the slot-click and drag-drop paths
 * @returns {{ startTime: string, endTime: string }}
 */
export function pixelYToSnappedTime(y, slotMinTime, slotHeight, slotDuration, eventDuration, snapInterval = 5) {
  const gridStartMin = timeToMinutes(slotMinTime);
  const minuteOffset = (y / slotHeight) * slotDuration;
  const clickedMin = gridStartMin + minuteOffset;
  const relativeMin = clickedMin - gridStartMin;
  const snappedMin = gridStartMin + Math.round(relativeMin / snapInterval) * snapInterval;
  const dur = eventDuration || slotDuration;
  return {
    startTime: minutesToTime(snappedMin),
    endTime: minutesToTime(snappedMin + dur),
  };
}

// ============ Event Positioning ============

/**
 * Calculate CSS position (top, height) for an event within a time grid column
 * @param {Object} apt - Appointment with start_time, end_time
 * @param {string} slotMinTime - Grid start time "HH:mm"
 * @param {number} slotHeight - Height of one slot in px
 * @param {number} slotDuration - Duration of one slot in minutes
 * @returns {{ top: number, height: number }}
 */
// Pixel offset of the current time within a time grid, or null when "now" is
// outside [slotMinTime, slotMaxTime). Shared by NowIndicator and the phone
// grid's scroll-to-now.
export function getNowTopPx(slotMinTime, slotMaxTime, slotHeight, slotDuration) {
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const gridStart = timeToMinutes(slotMinTime);
  const gridEnd = timeToMinutes(slotMaxTime);
  if (currentMinutes < gridStart || currentMinutes > gridEnd) return null;
  const pixelsPerMinute = slotHeight / slotDuration;
  return (currentMinutes - gridStart) * pixelsPerMinute;
}

export function getEventPosition(apt, slotMinTime, slotHeight, slotDuration) {
  const gridStart = timeToMinutes(slotMinTime);
  const eventStart = timeToMinutes(apt.start_time);
  const eventEnd = timeToMinutes(apt.end_time);

  const minutesFromTop = eventStart - gridStart;
  const durationMinutes = eventEnd - eventStart;

  const pixelsPerMinute = slotHeight / slotDuration;

  return {
    top: minutesFromTop * pixelsPerMinute,
    height: Math.max(durationMinutes * pixelsPerMinute, slotHeight * 0.5), // min half slot height
  };
}

// ============ Overlap Layout ============

export function computeOverlapLayout(appointments) {
  if (!appointments || appointments.length <= 1) return {};

  const events = appointments.map(apt => ({
    id: apt.id,
    start: timeToMinutes(apt.start_time),
    end: timeToMinutes(apt.end_time),
  }));

  events.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));

  // Build overlap groups
  const groups = [];
  let currentGroup = [events[0]];
  let groupEnd = events[0].end;

  for (let i = 1; i < events.length; i++) {
    if (events[i].start < groupEnd) {
      currentGroup.push(events[i]);
      groupEnd = Math.max(groupEnd, events[i].end);
    } else {
      groups.push(currentGroup);
      currentGroup = [events[i]];
      groupEnd = events[i].end;
    }
  }
  groups.push(currentGroup);

  const layout = {};

  for (const group of groups) {
    if (group.length === 1) continue;

    // Greedy column assignment
    const columns = [];
    for (const event of group) {
      let placed = false;
      for (let col = 0; col < columns.length; col++) {
        const lastInCol = columns[col][columns[col].length - 1];
        if (lastInCol.end <= event.start) {
          columns[col].push(event);
          placed = true;
          break;
        }
      }
      if (!placed) {
        columns.push([event]);
      }
    }

    const totalColumns = columns.length;

    for (let col = 0; col < columns.length; col++) {
      for (const event of columns[col]) {
        let span = 1;
        for (let nextCol = col + 1; nextCol < totalColumns; nextCol++) {
          const blocked = columns[nextCol].some(
            other => other.start < event.end && other.end > event.start
          );
          if (blocked) break;
          span++;
        }
        layout[event.id] = {
          columnIndex: col,
          totalColumns,
          left: (col / totalColumns) * 100,
          width: (span / totalColumns) * 100,
        };
      }
    }
  }

  return layout;
}

// ============ Color Utilities ============

/**
 * Get status color, with optional overrides from org settings
 */
export function getStatusColor(status, overrides) {
  if (overrides?.[status]) return overrides[status];
  return STATUS_COLORS[status] || STATUS_COLORS.scheduled;
}

// ============ Provider Colors ============

const PROVIDER_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#8B5CF6', // Purple
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#F97316', // Orange
];

/**
 * Get a consistent color for a provider based on their ID
 */
/**
 * Fill color for appointments flagged pn_due (progress note / reassessment due),
 * with its provenance so settings can say where the color came from.
 * Precedence: legend override > ACTIVE appointment type named Re-Eval /
 * Re-Evaluation (word start, so "Pre-Eval" does not match) > red.
 * Settings, the legend and EventCard all call this with the same full type
 * map; the active filter lives here so the three never disagree.
 *
 * @returns {{ color: string, source: 'custom'|'type'|'default', typeName?: string }}
 */
export function resolvePnDueColorSource(legendSettings, appointmentTypesMap) {
  if (legendSettings?.pn_due_color) return { color: legendSettings.pn_due_color, source: 'custom' };
  const reEvalType = Object.values(appointmentTypesMap || {}).find(
    t => t && t.is_active !== false && /\bre-?\s?eval/i.test(t.name || '')
  );
  if (reEvalType?.color) return { color: reEvalType.color, source: 'type', typeName: reEvalType.name };
  return { color: '#dc2626', source: 'default' };
}

export function resolvePnDueColor(legendSettings, appointmentTypesMap) {
  return resolvePnDueColorSource(legendSettings, appointmentTypesMap).color;
}

export function getProviderColor(providerId) {
  if (!providerId) return '#0facd7';
  let hash = 0;
  const idStr = String(providerId);
  for (let i = 0; i < idStr.length; i++) {
    hash = idStr.charCodeAt(i) + ((hash << 5) - hash);
  }
  return PROVIDER_COLORS[Math.abs(hash) % PROVIDER_COLORS.length];
}

/**
 * Get texture CSS properties for a pattern name
 */
export function getTextureCSS(pattern) {
  return TEXTURE_PATTERNS[pattern] || TEXTURE_PATTERNS.none;
}

// ============ Unavailability Calculation ============

/**
 * Calculate unavailable time ranges for a day based on schedule_template
 * @param {Object|null} daySchedule - { is_available, blocks: [{ start_time, end_time }] }
 * @param {string} slotMinTime - Grid start "HH:mm"
 * @param {string} slotMaxTime - Grid end "HH:mm"
 * @returns {Array<{ start: string, end: string }>}
 */
export function getUnavailableRanges(daySchedule, slotMinTime, slotMaxTime) {
  const gridStart = timeToMinutes(slotMinTime);
  const gridEnd = timeToMinutes(slotMaxTime);

  // If no schedule or not available, entire day is unavailable
  if (!daySchedule || !daySchedule.is_available) {
    return [{ start: slotMinTime, end: slotMaxTime }];
  }

  const blocks = daySchedule.blocks || [];
  if (blocks.length === 0) {
    // is_available is true but no explicit blocks — provider is available all day
    return [];
  }

  // Sort blocks by start time
  const sorted = [...blocks].sort(
    (a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time)
  );

  const unavailable = [];
  let cursor = gridStart;

  for (const block of sorted) {
    const blockStart = timeToMinutes(block.start_time);
    const blockEnd = timeToMinutes(block.end_time);

    if (blockStart > cursor) {
      unavailable.push({
        start: minutesToTime(Math.max(cursor, gridStart)),
        end: minutesToTime(Math.min(blockStart, gridEnd)),
      });
    }
    cursor = Math.max(cursor, blockEnd);
  }

  // Gap after last block
  if (cursor < gridEnd) {
    unavailable.push({
      start: minutesToTime(cursor),
      end: minutesToTime(gridEnd),
    });
  }

  return unavailable;
}

// ============ Synthetic Block Generation ============

/**
 * Generate synthetic appointment sub-blocks within real availability blocks.
 * When default_appointment_duration is set, this splits each availability block
 * into sub-blocks of that duration, skipping over blocked override ranges and
 * restarting cadence after each override.
 *
 * @param {Array<{start_time: string, end_time: string}>} blocks
 * @param {Array<{start: string, end: string}>} blockedRanges
 * @param {number} appointmentDuration - Minutes per synthetic block
 * @returns {Array<{start_time: string, end_time: string}>}
 */
export function generateSyntheticBlocks(blocks, blockedRanges, appointmentDuration) {
  if (!blocks || !blocks.length || !appointmentDuration) return blocks || [];

  const synthetic = [];

  const blocked = (blockedRanges || []).map(r => ({
    start: timeToMinutes(r.start),
    end: timeToMinutes(r.end),
  }));

  for (const block of blocks) {
    const blockStart = timeToMinutes(block.start_time);
    const blockEnd = timeToMinutes(block.end_time);
    let cursor = blockStart;

    while (cursor < blockEnd) {
      const overlapping = blocked.find(b => cursor >= b.start && cursor < b.end);
      if (overlapping) {
        cursor = overlapping.end;
        continue;
      }

      const nextBlocked = blocked
        .filter(b => b.start > cursor && b.start < blockEnd)
        .sort((a, b) => a.start - b.start)[0];

      const segmentEnd = nextBlocked ? Math.min(nextBlocked.start, blockEnd) : blockEnd;

      let subStart = cursor;
      while (subStart < segmentEnd) {
        const subEnd = Math.min(subStart + appointmentDuration, segmentEnd);
        synthetic.push({
          start_time: minutesToTime(subStart),
          end_time: minutesToTime(subEnd),
        });
        subStart = subEnd;
      }

      cursor = segmentEnd;
    }
  }

  return synthetic;
}

// ============ Date Formatting ============

/**
 * Format a Date object as "YYYY-MM-DD" using local timezone (not UTC)
 */
export function toLocalDateString(date) {
  if (typeof date === 'string') return date;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// ============ Day of Week Helpers ============

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Get the schedule_template key for a given Date
 */
export function getDayKey(date) {
  return DAY_NAMES[date.getDay()];
}

/**
 * Generate an array of dates for a week starting from a given date
 */
/**
 * Dates visible in a week grid for a days-in-week setting: 3 = rolling
 * date..date+2, 5 = Mon-Fri, 6 = Mon-Sat, otherwise Sun-Sat. Same logic as the
 * desktop CustomTimeGrid; shared by the phone grid, its header title and print.
 */
export function getVisibleWeekDates(date, daysInWeek) {
  if (daysInWeek === 3) {
    const start = new Date(date);
    return [0, 1, 2].map(i => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }
  const week = getWeekDates(date);
  if (daysInWeek === 6) return week.slice(1);
  if (daysInWeek === 5) return week.slice(1, 6);
  return week;
}

export function getWeekDates(startDate) {
  const dates = [];
  const start = new Date(startDate);
  // Go to Sunday of the week
  start.setDate(start.getDate() - start.getDay());
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    dates.push(d);
  }
  return dates;
}

/**
 * Format a date for display
 */
export function formatDateHeader(date, view) {
  const d = new Date(date);
  if (view === 'day') {
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Generate time slot labels for the time column
 * @param {string} minTime - "HH:mm"
 * @param {string} maxTime - "HH:mm"
 * @param {number} slotDuration - minutes per slot
 * @returns {Array<{ time: string, label: string }>}
 */
export function generateTimeSlots(minTime, maxTime, slotDuration) {
  const start = timeToMinutes(minTime);
  const end = timeToMinutes(maxTime);
  const slots = [];

  for (let t = start; t < end; t += slotDuration) {
    slots.push({
      time: minutesToTime(t),
      label: formatTimeDisplay(minutesToTime(t)),
    });
  }

  return slots;
}

/**
 * Waitlist matches per provider for a date range: every bookable start (per
 * the shared availability engine, so notes, holidays, overrides and scheduled
 * template changes agree with the wizard) that at least one active waitlist
 * entry accepts.
 *
 * Entries' time preferences are normalized (legacy presets / specific_time or
 * the windows shape) and their window starts are anchored as candidates, so a
 * 10:00 preference stripes at 10:00 even when the duration cadence skips it.
 *
 * @param {Array} entries - Active waitlist entries
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD (inclusive)
 * @param {Array} providerAvailabilities - provider_availability rows (already
 *   scoped to the calendar's location by the store; not filtered again here)
 * @param {Array} existingAppointments
 * @param {string} slotMinTime - unused (kept for the caller's signature)
 * @param {string} slotMaxTime - unused (kept for the caller's signature)
 * @param {number} slotDuration
 * @param {number} appointmentDuration - cadence (org default appointment length)
 * @param {Object} overridesMap - provider id -> schedule_override rows
 * @param {string|null} effectiveLocationId - entry location filter
 * @param {Set<string>} [holidayDates] - org holidays (buildHolidaySet)
 * @returns {Map<string, Array<{ date: string, startTime: string, endTime: string, candidates: Array }>>}
 */
export function buildWaitlistMatchMap(entries, startDate, endDate, providerAvailabilities, existingAppointments, slotMinTime, slotMaxTime, slotDuration, appointmentDuration, overridesMap, effectiveLocationId, holidayDates) {
  if (!entries?.length || !startDate || !endDate) return new Map();

  // provider_ids arrives enriched ([{_provider: {...}}]) from the API or as
  // plain id strings from local seeds; normalize once, outside the loops
  const preparedEntries = entries.map(entry => ({
    entry,
    providerIdList: normalizeWaitlistProviderIds(entry.provider_ids),
    constraints: normalizeTimePreferences(entry.time_preferences),
  }));
  const anchors = [...new Set(preparedEntries.flatMap(p => p.constraints.windows.map(w => w.start)))];

  const rowsByProvider = new Map();
  for (const avail of (providerAvailabilities || [])) {
    if (!avail?.provider_id) continue;
    if (!rowsByProvider.has(avail.provider_id)) rowsByProvider.set(avail.provider_id, []);
    rowsByProvider.get(avail.provider_id).push(avail);
  }

  const apptsByProviderDate = new Map();
  for (const apt of (existingAppointments || [])) {
    if (!apt?.provider_id || !apt.appointment_date) continue;
    const key = `${apt.provider_id}|${apt.appointment_date}`;
    if (!apptsByProviderDate.has(key)) apptsByProviderDate.set(key, []);
    apptsByProviderDate.get(key).push(apt);
  }

  const totalDays = Math.round((new Date(endDate + 'T12:00:00') - new Date(startDate + 'T12:00:00')) / 86400000) + 1;
  if (totalDays <= 0) return new Map();
  const iterDuration = appointmentDuration || slotDuration;
  const now = new Date();
  const todayStr = localDateString(now);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const matchMap = new Map();

  for (const [providerId, rows] of rowsByProvider) {
    const availMap = buildAvailabilityMap(rows, overridesMap?.[providerId] || [], startDate, totalDays, { holidayDates });

    for (let i = 0; i < totalDays; i++) {
      const dateStr = engineAddDays(startDate, i);
      const blocks = availMap.get(dateStr) || [];
      if (blocks.length === 0) continue;
      const freeBlocks = subtractAppointments(blocks, apptsByProviderDate.get(`${providerId}|${dateStr}`) || []);
      const starts = findCandidateStarts(freeBlocks, iterDuration, anchors);

      for (const slotStart of starts) {
        if (dateStr === todayStr && slotStart < nowMinutes) continue;
        const slotEnd = slotStart + iterDuration;

        const candidates = preparedEntries.filter(({ entry, providerIdList, constraints }) => {
          if (entry.location_id && effectiveLocationId && entry.location_id !== effectiveLocationId) return false;
          if (entry.earliest_date && dateStr < entry.earliest_date) return false;
          if (entry.latest_date && dateStr > entry.latest_date) return false;
          // Any selected providers constrain the match, regardless of the
          // preference label (legacy 'preferred' entries count as specific)
          if (providerIdList.length && !providerIdList.includes(providerId)) return false;
          return isSlotAllowedByConstraints(slotStart, slotEnd, dateStr, constraints);
        });

        if (candidates.length > 0) {
          if (!matchMap.has(providerId)) matchMap.set(providerId, []);
          matchMap.get(providerId).push({
            date: dateStr,
            startTime: minutesToTime(slotStart),
            endTime: minutesToTime(slotEnd),
            candidates: sortWaitlistEntries(candidates.map(c => c.entry)),
          });
        }
      }
    }
  }

  return matchMap;
}

export { formatPatientName } from '../../utils/patientName';

/**
 * Stable sort of provider objects by a saved id order (per-user preference).
 * Providers not in the order keep their incoming (API) order at the end.
 */
export function sortProvidersByOrder(providers, order) {
  if (!order?.length) return providers;
  const idx = new Map(order.map((id, i) => [id, i]));
  return [...providers].sort(
    (a, b) => (idx.has(a.id) ? idx.get(a.id) : Infinity) - (idx.has(b.id) ? idx.get(b.id) : Infinity)
  );
}

/**
 * Format a date-only birthday string (YYYY-MM-DD) as MM/DD/YYYY.
 * Pure string manipulation — never new Date(), which shifts date-only
 * strings by a day in UTC-negative timezones.
 */
export function formatBirthdayMDY(birthday) {
  if (!birthday || typeof birthday !== 'string') return '';
  const parts = birthday.split('T')[0].split('-');
  if (parts.length !== 3) return birthday;
  const [y, m, d] = parts;
  return `${m}/${d}/${y}`;
}
